import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  endAt,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAt,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { variantesDaBusca } from '@/lib/telefone';
import { paraDocumento } from '@/conversas/etiquetas';
import { tratarErro, tratarErroDeCallable } from '@/lib/erros';

/**
 * ★★★ WT lote 1 — TODA CALLABLE PASSA POR AQUI.
 *
 * ⚠⚠ Erro de callable chega ao cliente com o `code` do servidor (`permission-denied`,
 * `failed-precondition`, `resource-exhausted`) — e é **o diagnóstico mais valioso que
 * existe**: ele diz que a tela pediu algo que o servidor recusou. ★ Até agora ele só
 * aparecia na mensagem que a pessoa lia e esquecia.
 *
 * ★ O embrulho **registra e relança**: quem chama continua tratando o erro como antes
 * (as telas leem `e.code` para escolher a mensagem). ⚠ Engolir aqui quebraria todas elas.
 */
const chamarCallable = (nome) => async (dados) => {
  try {
    return await httpsCallable(functions, nome)(dados);
  } catch (erro) {
    throw tratarErroDeCallable(nome, erro);
  }
};


const colConversas = (clinicaId) => collection(db, 'clinicas', clinicaId, 'conversas');

/**
 * Conversas de um espaço. O filtro por espaço não é conveniência: as security
 * rules avaliam documento a documento, e uma listagem sem ele é recusada.
 */
/**
 * Procura conversas no BANCO, não só entre as carregadas.
 *
 * A assinatura da lista traz as 200 mais recentes (`ouvirConversas`), e a busca
 * filtrava só essas. Depois da importação dos 90 dias de histórico, centenas de
 * conversas encerradas ficaram fora dessa janela: existem no banco, com nome e
 * telefone, mas a recepção não as achava de jeito nenhum.
 *
 * ⚠ **O filtro por `espaco` não é conveniência, é o que torna a query legal.**
 * As `firestore.rules` avaliam `list` documento a documento (`allow list: if
 * podeAtender() && doEspacoDaConversa()`): basta um documento de outro espaço no
 * resultado para a query INTEIRA ser recusada. Também é o que garante que a
 * recepção nunca vê conversa de vendas.
 *
 * Devolve `[]` em qualquer falha, de propósito: a busca é um complemento à lista
 * já em memória, e derrubar a tela por causa de um índice faltando seria pior
 * que devolver menos resultados.
 */
export async function buscarConversas(clinicaId, espaco, termo) {
  const texto = String(termo ?? '').trim();
  if (texto.length < 3) return [];

  const consultas = [];

  /**
   * 1. TELEFONE — o caminho confiável.
   *
   * Igualdade exata contra as duas grafias do celular brasileiro. É o que
   * resolve o caso real: a recepção tem o número do paciente na mão.
   */
  const variantes = variantesDaBusca(texto);
  if (variantes.length) {
    consultas.push(
      getDocs(query(
        colConversas(clinicaId),
        where('espaco', '==', espaco),
        where('contatoTelefone', 'in', variantes),
        limit(20),
      )),
    );
  }

  /**
   * 2. NOME — aproximado, e é honesto dizer que é.
   *
   * O Firestore não faz "contém no meio": só PREFIXO, por range. E o range é
   * sensível a maiúsculas — `contatoNome` guarda o `pushName` do WhatsApp
   * ("Olavo Pires"), então "maria" em minúsculas NÃO casa.
   *
   * Por isso vão duas tentativas: o termo como digitado e com a primeira letra
   * maiúscula, que cobre o jeito como a maioria digita. Continua sem achar
   * sobrenome no meio do nome — para isso só um campo normalizado no banco, que
   * exigiria backfill e não foi autorizado.
   */
  const porPrefixo = (t) => getDocs(query(
    colConversas(clinicaId),
    where('espaco', '==', espaco),
    orderBy('contatoNome'),
    startAt(t),
    endAt(`${t}\uf8ff`),
    limit(20),
  ));
  const capitalizado = texto.charAt(0).toUpperCase() + texto.slice(1);
  consultas.push(porPrefixo(texto));
  if (capitalizado !== texto) consultas.push(porPrefixo(capitalizado));

  /**
   * ★★★ WT lote 1 — O RESULTADO DIZ SE ESTÁ COMPLETO.
   *
   * ⚠⚠ Era `continue` com um `console.error`: uma das consultas podia ser **recusada** e a
   * busca devolvia a outra como se fosse tudo. ★ Na tela: **menos resultados, sem erro
   * nenhum** — a assinatura exata do defeito da peça WC, onde a recusa virava ausência.
   *
   * ★ Agora a lista carrega `incompleta` e a tela avisa. ⚠ Devolver `[]` ou lançar seria
   * pior: a pessoa perderia o que DEU para achar.
   */
  const resultados = await Promise.allSettled(consultas);
  const achadas = new Map();
  let incompleta = false;
  for (const r of resultados) {
    if (r.status !== 'fulfilled') {
      incompleta = true;
      tratarErro('busca de conversas', null, 'service')(r.reason);
      continue;
    }
    for (const d of r.value.docs) achadas.set(d.id, { id: d.id, ...d.data() });
  }
  const lista = [...achadas.values()];
  // ★ marca no ARRAY, não num objeto novo: todo chamador já trata isto como lista, e
  // trocar a forma do retorno quebraria os cinco pontos que a consomem.
  lista.incompleta = incompleta;
  return lista;
}

/**
 * ★ VM — a conversa de WhatsApp VINCULADA a um lead (ou `null`).
 *
 * Consulta por `espaco` + `leadId` (igualdade + igualdade), `limit(1)`.
 * Vivia dentro do `PainelLead`; saiu para cá para o teste de tela poder dublar
 * "lead com conversa" e "lead sem conversa" sem tocar no Firestore.
 *
 * ★ WC — o filtro de `espaco` é OBRIGATÓRIO, e não por desempenho: a `allow list` de `conversas` decide
 * pela FORMA DA CONSULTA, e sem restringir `espaco` ela é recusada para todo papel que não seja gestor
 * (medido em `docs/wc-medicao.md`, inclusive com a coleção vazia). Antes disso, esta função falhava para
 * toda SDR e a falha era ENGOLIDA no `catch` de quem chama: o botão "Abrir conversa" simplesmente não
 * aparecia — ausência, não erro, e por isso ninguém reportou.
 *
 * `'vendas'` é fixo de propósito (decisão do dono): o único chamador é a ficha do lead, que é de vendas, e
 * é o que o gestor já enxergava. Quem precisar de outro espaço um dia tem de mudar isto CONSCIENTEMENTE —
 * generalizar a assinatura agora seria abrir a porta sem ninguém precisar dela.
 */
/**
 * ★★★ WS — A CONVERSA DO LEAD, EM QUALQUER ESPAÇO — **uma consulta POR ESPAÇO**.
 *
 * ⚠⚠ Esta consulta tinha `where('espaco','==','vendas')` fixo, e foi assim que a conversa da
 * Débora desapareceu da ficha: ela nasceu em `vendas` e **foi TRANSFERIDA** para a recepção
 * em 30/09 (`transferirConversa` reescreve o `espaco` e deixa `numeroId` como estava). Do
 * minuto da transferência em diante a ficha dizia *"nunca falou com a clínica"* sobre uma
 * conversa de 50 mensagens — `docs/medicao-conversa-ficha.md`, Parte 4.
 *
 * ★★★ POR QUE NÃO SIMPLESMENTE TIRAR O FILTRO DE ESPAÇO — e isto é MEDIDO, não suposto.
 *
 * Tirar o filtro é o que parece óbvio, e **recria o defeito da WC**. Para `list`, o motor de
 * rules tem de **provar pela forma da consulta** que `doEspacoDaConversa()` vale em todo
 * documento devolvido; como a regra fala de `resource.data.espaco`, consulta que não
 * restringe `espaco` não é provável e é **RECUSADA** — para todo papel que não seja gestor.
 * ⚠ O gestor passa (curto-circuita em `isGestor`), e foi por isso que o defeito original
 * ficou sete semanas invisível para quem administra.
 *
 * ★ Medido no emulador contra as rules reais, nas duas formas (com e sem `orderBy`):
 * SDR **recusada** sem o filtro; SDR **autorizada** pedindo `vendas` e **também** pedindo
 * `recepcao` (a VU pôs `sdr` em `atendeRecepcao`). ★★ **Daí o desenho: duas consultas, cada
 * uma restringindo o seu espaço** — a regra continua satisfeita e a ficha passa a ver os
 * dois lados.
 *
 * ★ Sem `orderBy` e sem `limit`: são só igualdades, então **nenhum índice composto novo** é
 * preciso, e um lead tem poucas conversas. A mais recente é escolhida aqui, em memória —
 * com `limit(1)` sem ordenação o Firestore devolveria uma qualquer, e o DARLAN (medido) tem
 * duas.
 *
 * ⚠ Cada espaço vai no SEU `try`: a recepcionista é recusada em `vendas` (medido, caso WC),
 * e uma recusa num espaço não pode apagar o resultado do outro.
 */
const ESPACOS_DO_LEAD = ['vendas', 'recepcao'];
const instante = (c) => c?.atualizadoEm?.toMillis?.() ?? c?.atualizadoEm?.seconds ?? 0;

/**
 * ★★★ A-3(b), 11/out/2026 — O ESPAÇO PASSOU A SER PARÂMETRO, com o padrão de antes.
 *
 * A agenda precisa da conversa **do próprio setor**: quem é de vendas abre a
 * conversa de vendas, não a da recepção que por acaso é mais recente. ⚠ E a
 * ficha do lead continua querendo *"a conversa, onde ela estiver"* — foi o
 * conserto da WS, e mexer nisso recriaria o defeito da Débora.
 *
 * ★ Então `espacos` é opcional e o padrão é `ESPACOS_DO_LEAD` (os dois): quem
 * não passa nada recebe exatamente o comportamento da WS. ★★ A lista fechada
 * continua sendo a fonte do `where` — é ela que impede o filtro de virar
 * decoração e cair na recusa da `allow list` (guarda em `wc.guardas.test.js`).
 */
export async function buscarConversaDoLead(clinicaId, leadId, espacos = ESPACOS_DO_LEAD) {
  if (!leadId) return null;
  // ⚠ Espaço desconhecido NÃO passa: um `where('espaco','==',undefined)` lançaria
  // no SDK, e um `where` com valor que nenhuma conversa tem devolveria "não
  // existe" — a afirmação que a WT proibiu fazer sem ter olhado.
  // ⚠ Escrito a partir da LISTA FECHADA, e não filtrando o que chegou: assim a
  // ordem é sempre a canônica e nenhum valor de fora entra num `where`.
  // ★ E a forma importa por um segundo motivo: `const alvos = (…)` fazia o
  // varredor da guarda WC (`wc.guardas.test.js:contexto`) atribuir a consulta a
  // "alvos" em vez de a `buscarConversaDoLead`, e a SANIDADE dela caía — a
  // armadilha que o comentário dessa guarda já descrevia.
  const alvos = ESPACOS_DO_LEAD.filter((e) => (espacos ?? ESPACOS_DO_LEAD).includes(e));
  if (alvos.length === 0) return null;
  /**
   * ★★★ WT lote 1 — "NÃO ACHEI" NÃO PODE VIRAR "NÃO EXISTE". **Conserto de defeito meu.**
   *
   * ⚠⚠ A versão da WS tinha um `try` por espaço que devolvia `[]` na recusa e seguia. ★ Com
   * os dois espaços recusados, a função devolvia `null` — e `null`, para a ficha, significa
   * *"este lead nunca falou com a clínica"*. **Era o defeito da WC de novo**, agora
   * recriado dentro do conserto dele.
   *
   * ★★ Quem denunciou foi o teste de tela da WT (a recepcionista abrindo a ficha), não o
   * olho: no dublê antigo nada era recusado, e a falha não tinha como aparecer.
   *
   * ★★★ A regra: só se afirma ausência quando **TODOS os espaços responderam** e todas as
   * respostas vieram vazias. ⚠ Se **qualquer um** falhou e nada foi achado, a função
   * **lança** — e a ficha mostra *"não foi possível verificar"*.
   *
   * ⚠⚠ Exigir que TODOS respondam, e não "ao menos um", foi a segunda correção desta mesma
   * função: com um espaço recusado, a ausência **não é conhecida** — e o espaço recusado é
   * justamente o que mais provavelmente tem a conversa. ★ Achar é prova positiva (basta um
   * espaço responder); **não achar não é prova de nada** se faltou olhar.
   */
  let falharam = 0;
  const porEspaco = await Promise.all(alvos.map(async (espaco) => {
    try {
      const snap = await getDocs(query(
        colConversas(clinicaId),
        where('espaco', '==', espaco),
        where('leadId', '==', leadId),
      ));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
      // ★ Um espaço que este papel não alcança é o recorte dele, não erro — mas CONTA.
      falharam += 1;
      // ★ WT 1b: contar já era o tratamento (quem chama lança); o registro entra aqui.
      tratarErro(`as conversas do lead em ${espaco}`, null, 'service')(e);
      return [];
    }
  }));
  const todas = porEspaco.flat();
  if (todas.length === 0) {
    if (falharam > 0) {
      const erro = new Error('Não foi possível consultar as conversas deste lead em nenhum espaço.');
      erro.code = 'conversas-do-lead-indisponiveis';
      throw erro;
    }
    return null;
  }
  // ★ A mais recente: é a que tem a conversa viva, não a primeira que o índice devolver.
  return todas.reduce((maior, c) => (instante(c) > instante(maior) ? c : maior));
}

/**
 * ★★★ WS — UMA conversa pelo id, para a seleção que veio de fora da lista.
 *
 * ⚠⚠ O inbox assina as **200** mais recentes (`ouvirConversas`), e a seleção só valia
 * dentro dessa lista. Medido: das 1.102 conversas de `vendas`, a 200ª corta em 01/10 —
 * **127 dos 220** leads com tarefa aberta têm a conversa FORA do corte, e o botão da ficha
 * levava a tela a dizer *"Escolha uma conversa à esquerda"*. Era a causa de maior alcance,
 * e não havia nada errado no banco (`docs/medicao-conversa-ficha.md`, Parte 5).
 *
 * ★ Leitura por id, não consulta: não paga índice e não depende de ordenação.
 * ⚠ Devolve o documento CRU — quem chama é que confere espaço e clínica.
 */
export async function buscarConversaPorId(clinicaId, conversaId) {
  if (!clinicaId || !conversaId) return null;
  const snap = await getDoc(doc(db, 'clinicas', clinicaId, 'conversas', conversaId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function ouvirConversas(clinicaId, espaco, aoReceber, aoFalhar) {
  const q = query(
    colConversas(clinicaId),
    where('espaco', '==', espaco),
    orderBy('atualizadoEm', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('as conversas', aoFalhar),
  );
}

/**
 * ★★★ AS 300 MAIS RECENTES — não as 300 mais antigas.
 *
 * ## O defeito, medido no canário de 07/out/2026
 *
 * ⚠⚠ A consulta era `orderBy('enviadaEm', 'asc') + limit(300)`. ★★★ **`asc` + `limit` dá as
 * PRIMEIRAS, não as últimas** — então numa conversa com mais de 300 mensagens a tela assinava as
 * **300 mais antigas** e **as novas nunca apareciam**.
 *
 * ★ O dono mandou 20 mensagens de teste numa conversa de 320: **nenhuma apareceu**, e a
 * **posição da primeira era 301** — a janela terminava exatamente onde o teste começava. ⚠ A
 * prévia da lista atualizava (ela vem do documento da CONVERSA, assinado inteiro), o que fazia
 * o sintoma parecer defeito de envio. **Não era: as mensagens estavam todas gravadas.**
 *
 * ⚠⚠ E era **pré-existente**: a consulta vem de `d259237` ("Fase 2 — UI da central de
 * conversas"), tão velha quanto o inbox. Alcance medido em produção: **5 conversas** acima de
 * 300 — a maior com **933 mensagens e 633 invisíveis**, parada em agosto para quem a atende.
 *
 * ## ★★ Por que inverter EM MEMÓRIA e não entregar `desc` à tela
 *
 * ★★★ `comDivisorias` (`conversas/dias.js`) percorre a lista **em ordem** e insere a divisória
 * quando o dia muda. ⚠ Com a lista descendente ela produziria "Hoje" no topo e as divisórias
 * nos lugares errados. ★ Então o `reverse()` aqui é parte do conserto, não enfeite: **o
 * contrato com a tela continua sendo ordem CRESCENTE.**
 *
 * ⚠ O corte de 300 **não desaparece** — ele passa a cortar o lado certo. ★★ Rolar para cima e
 * ver agosto numa conversa de 933 exige **paginação**, que é peça própria.
 */
export function ouvirMensagens(clinicaId, conversaId, aoReceber, aoFalhar) {
  const q = query(
    collection(db, 'clinicas', clinicaId, 'conversas', conversaId, 'mensagens'),
    // ★★★ DESC para pegar as mais RECENTES; a tela recebe invertido, logo abaixo.
    orderBy('enviadaEm', 'desc'),
    limit(300),
  );
  return onSnapshot(
    q,
    (snap) => {
      const recentes = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // ⚠ `reverse()` muta o array local, que é descartável — e devolve ordem crescente.
      aoReceber(recentes.reverse());
    },
    tratarErro('as mensagens', aoFalhar),
  );
}

export function ouvirEtiquetas(clinicaId, aoReceber, aoFalhar) {
  return onSnapshot(
    collection(db, 'clinicas', clinicaId, 'etiquetas'),
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('as etiquetas', aoFalhar),
  );
}

/**
 * Etiquetas: criar, editar e remover.
 *
 * Sem callable — as `firestore.rules` já restringem a escrita ao gestor
 * (`match /etiquetas/{etiquetaId}` → `allow write: if isGestor(clinicaId)`), e
 * não há regra de negócio no servidor a impor. A tela esconde os controles de
 * quem não pode, para não expor um botão que só falharia.
 *
 * `paraDocumento` saneia nome, cor e espaço antes de gravar: é este objeto que
 * chega ao banco, e um campo torto só apareceria depois, como chip invisível.
 */
const colEtiquetas = (clinicaId) => collection(db, 'clinicas', clinicaId, 'etiquetas');

/**
 * Cria com id AUTOMÁTICO, e não com um slug do nome.
 *
 * O id vai parar dentro de `conversas.etiquetas[]`. Um slug colidiria entre duas
 * etiquetas de mesmo nome, e a segunda sobrescreveria a primeira em silêncio —
 * levando cor e espaço junto, em todas as conversas que já a usavam.
 */
export async function criarEtiqueta(clinicaId, dados) {
  // ⚠ `documento`, não `doc`: `doc` é a função do Firestore importada no topo
  // deste arquivo. Sombreá-la aqui funcionava por acaso — esta função não
  // precisa de `doc()` hoje. No dia em que precisasse, seria "doc is not a
  // function". É a mesma forma do bug que derrubou a navegação da agenda.
  const documento = paraDocumento(dados);
  if (!documento.nome) throw new Error('Dê um nome à etiqueta.');
  const ref = await addDoc(colEtiquetas(clinicaId), documento);
  return ref.id;
}

/** Só nome, cor e espaço mudam — o id é o que as conversas guardam. */
export async function atualizarEtiqueta(clinicaId, etiquetaId, dados) {
  const dadosLimpos = paraDocumento(dados);
  if (!dadosLimpos.nome) throw new Error('Dê um nome à etiqueta.');
  await updateDoc(doc(db, 'clinicas', clinicaId, 'etiquetas', etiquetaId), dadosLimpos);
}

/**
 * Remove a etiqueta — e SÓ ela.
 *
 * As conversas que a usavam NÃO são varridas. Elas continuam com o id no array
 * `conversa.etiquetas`, e isso é inofensivo: tanto o seletor do `PainelContato`
 * quanto os chips da lista são desenhados a partir da COLEÇÃO de etiquetas, não
 * do array da conversa — um id que não existe mais simplesmente não encontra
 * etiqueta e some sozinho da tela.
 *
 * Varrer custaria um update em cada conversa que já usou a etiqueta (centenas,
 * hoje) para um ganho puramente estético em dado que ninguém lê. O efeito
 * colateral, dito na confirmação da tela: recriar uma etiqueta com o mesmo nome
 * não ressuscita as marcações antigas, porque o id novo é outro.
 */
export async function removerEtiqueta(clinicaId, etiquetaId) {
  await deleteDoc(doc(db, 'clinicas', clinicaId, 'etiquetas', etiquetaId));
}

export function ouvirRespostasRapidas(clinicaId, aoReceber, aoFalhar) {
  return onSnapshot(
    collection(db, 'clinicas', clinicaId, 'respostasRapidas'),
    (snap) => {
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.atalho ?? '').localeCompare(b.atalho ?? ''));
      aoReceber(lista);
    },
    tratarErro('as respostas rápidas', aoFalhar),
  );
}

/**
 * Respostas rápidas: criar, editar e remover.
 *
 * Mesma lógica das etiquetas — as rules já restringem a escrita ao gestor
 * (`match /respostasRapidas/{respostaId}`), e não há regra de negócio no
 * servidor a impor.
 *
 * O `atalho` é o que o atendente digita depois da `/` no Composer, então ele é
 * saneado: sem espaço (o seletor casa por prefixo até o primeiro espaço) e
 * sempre começando com `/`, para a lista da tela e o que se digita coincidirem.
 */
const colRespostas = (clinicaId) => collection(db, 'clinicas', clinicaId, 'respostasRapidas');

// 'ambos' saiu junto com o das etiquetas: cada resposta é de um setor.
const ESPACOS_RESPOSTA = new Set(['vendas', 'recepcao']);

export function paraDocumentoResposta({ atalho, titulo, texto, espaco }) {
  const semBarra = String(atalho ?? '')
    .trim()
    .replace(/^\/+/, '')
    .replace(/\s+/g, '-')
    .slice(0, 24);
  return {
    atalho: semBarra ? `/${semBarra}` : '',
    titulo: String(titulo ?? '').replace(/\s+/g, ' ').trim().slice(0, 60),
    texto: String(texto ?? '').trim().slice(0, 4096),
    espaco: ESPACOS_RESPOSTA.has(String(espaco ?? '')) ? String(espaco) : 'recepcao',
  };
}

export async function criarRespostaRapida(clinicaId, dados) {
  // ⚠ Mesmo caso do `criarEtiqueta` acima: `doc` é a função do Firestore.
  const documento = paraDocumentoResposta(dados);
  if (!documento.atalho) throw new Error('Informe o atalho.');
  if (!documento.texto) throw new Error('Escreva o texto da resposta.');
  const ref = await addDoc(colRespostas(clinicaId), documento);
  return ref.id;
}

export async function atualizarRespostaRapida(clinicaId, respostaId, dados) {
  const dadosLimpos = paraDocumentoResposta(dados);
  if (!dadosLimpos.atalho) throw new Error('Informe o atalho.');
  if (!dadosLimpos.texto) throw new Error('Escreva o texto da resposta.');
  await updateDoc(doc(db, 'clinicas', clinicaId, 'respostasRapidas', respostaId), dadosLimpos);
}

export async function removerRespostaRapida(clinicaId, respostaId) {
  await deleteDoc(doc(db, 'clinicas', clinicaId, 'respostasRapidas', respostaId));
}

export function ouvirNumeros(clinicaId, aoReceber, aoFalhar) {
  return onSnapshot(
    collection(db, 'clinicas', clinicaId, 'numeros'),
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('os números', aoFalhar),
  );
}

// ---------------------------------------------------------------- escritas
// Mensagem nunca é gravada pelo cliente: as rules a deixam somente-leitura e
// tudo passa pelas Cloud Functions.

export const enviarMensagem = (dados) => chamarCallable('enviarMensagem')(dados);

/**
 * ★ WB — tenta de novo uma mensagem que NÃO saiu (`entrega: 'falha'`). É callable porque reaproveita o mesmo
 * documento da mensagem, e o cliente não escreve em `mensagens` (rules).
 */
export const reenviarMensagem = (dados) => chamarCallable('reenviarMensagem')(dados);

/**
 * Envia um arquivo já no Storage. Recebe o CAMINHO, não o binário — quem sobe
 * o arquivo é o `midiaService`, antes desta chamada.
 */
export const enviarMidia = (dados) => chamarCallable('enviarMidia')(dados);

/**
 * Reage a uma mensagem com um emoji. `emoji: ''` remove a reação.
 *
 * Não cria mensagem: o efeito é um campo no doc da mensagem-alvo, e o selo
 * aparece pela assinatura que o Thread já tem.
 */
export const enviarReacao = (dados) => chamarCallable('enviarReacao')(dados);

/**
 * Reenvia uma mensagem para outra conversa ou para um número digitado.
 *
 * Manda só os IDS: o conteúdo (texto e caminho da mídia) sai do documento de
 * origem, no servidor. É o que impede a tela de encaminhar um arquivo qualquer
 * do bucket com aparência de encaminhamento legítimo.
 */
export const encaminharMensagem = (dados) =>
  chamarCallable('encaminharMensagem')(dados);
export const transferirConversa = (dados) =>
  chamarCallable('transferirConversa')(dados);
export const resolverConversa = (dados) => chamarCallable('resolverConversa')(dados);
export const simularMensagemRecebida = (dados) =>
  chamarCallable('simularMensagemRecebida')(dados);

/**
 * Apaga um grupo e TODAS as suas mensagens. IRREVERSÍVEL.
 *
 * As travas de verdade estão no servidor — só gestor, e só conversa com
 * `ehGrupo === true`. Esconder o botão na tela evita o clique errado; recusar na
 * callable é o que protege o histórico de paciente.
 */
export const excluirGrupo = (dados) => chamarCallable('excluirGrupo')(dados);

/**
 * Abre uma conversa a partir de um telefone digitado, sem esperar o contato
 * escrever. Não envia mensagem: devolve o id para a tela abrir a conversa, e a
 * primeira mensagem sai pelo `enviarMensagem` de sempre.
 */
export const iniciarConversa = (dados) => chamarCallable('iniciarConversa')(dados);

/**
 * Envia UMA confirmação de agenda. O lote e a cadência entre pacientes ficam na
 * tela: um envio por chamada é o que permite espaçar as mensagens em vez de
 * despejá-las todas de uma vez pelo número da recepção.
 */
export const enviarConfirmacaoAgenda = (dados) =>
  chamarCallable('enviarConfirmacaoAgenda')(dados);

/**
 * Vincula (ou cria) o lead de uma conversa de VENDAS pelo painel do contato.
 * `{ clinicaId, conversaId, leadId? }` → `{ leadId, criado }`. Callable porque
 * as rules proíbem o cliente de gravar `leadId` na conversa; as guardas
 * (papel de vendas, espaço, grupo, já vinculada) moram no servidor.
 */
export const vincularLeadConversa = (dados) =>
  chamarCallable('vincularLeadConversa')(dados);

/** Campos de atendimento que as rules permitem ao próprio atendente. */
export async function atualizarConversa(clinicaId, conversaId, campos) {
  await updateDoc(doc(db, 'clinicas', clinicaId, 'conversas', conversaId), campos);
}

export const marcarLida = (clinicaId, conversaId) =>
  atualizarConversa(clinicaId, conversaId, { naoLidas: 0 });

/**
 * ★ WA — assumir passou a ser CALLABLE.
 *
 * Era um `update` direto do cliente. Com o rodízio, assumir uma conversa cuja RESERVA expirou leva o lead
 * junto (troca o SDR e escreve a timeline) — três escritas que precisam ser um ato só, e a recepcionista
 * nem pode escrever em `leads`. A limpeza dos campos da reserva também é do servidor (as rules proíbem o
 * cliente de tocá-los). O botão da lista chama isto; a autoridade sobre a reserva continua nas rules.
 */
export const assumirConversa = async (clinicaId, conversaId) => {
  const { data } = await chamarCallable('assumirAtendimento')({ clinicaId, conversaId });
  return data;
};

export const definirEtiquetas = (clinicaId, conversaId, etiquetas) =>
  atualizarConversa(clinicaId, conversaId, { etiquetas });

// VW: Reabrir deixa a conversa SEM dono → Aguardando, até alguém assumir (decisão do dono).
export const reabrirConversa = (clinicaId, conversaId) =>
  atualizarConversa(clinicaId, conversaId, { status: 'aberta', responsavelId: null, responsavelNome: null });
